/**
 * static/js/utils/body-lock.js
 *
 * Lock / unlock body scroll. When a full-screen modal or sheet opens on mobile,
 * the underlying page should not scroll-chain when the user scrolls inside the
 * modal. This generalises the `body:has(#showthread.checked) { overflow: hidden }`
 * pattern from messaging into a JS-callable primitive that any UI surface can use.
 *
 * Stacking semantics: lock() and unlock() ref-count, so multiple modals open
 * concurrently each call lock() and unlock() exactly once; body becomes
 * scrollable again only when the count returns to zero.
 *
 * Loaded per-page (not in base.html). No auto-side-effects unless
 * autoLockOnDialog() is called.
 *
 * Usage:
 *   JN.bodyLock.lock();   // disable body scroll
 *   JN.bodyLock.unlock(); // re-enable (after the matching lock)
 *   JN.bodyLock.autoLockOnDialog();  // auto-lock when any <dialog> opens via .open
 *
 * Refs #3886 (Mobile UI/UX Phase 2 PR 2.1).
 */
(function (window, document) {
  'use strict';

  var lockCount = 0;
  var savedOverflow = null;
  var savedPaddingRight = null;

  function getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }

  function lock() {
    if (lockCount === 0) {
      savedOverflow = document.body.style.overflow;
      savedPaddingRight = document.body.style.paddingRight;
      var sbw = getScrollbarWidth();
      document.body.style.overflow = 'hidden';
      // Compensate scrollbar disappearance to prevent layout shift on desktop.
      // Mobile browsers typically use overlay scrollbars (sbw === 0), so this is a no-op there.
      if (sbw > 0) {
        document.body.style.paddingRight = sbw + 'px';
      }
    }
    lockCount += 1;
  }

  function unlock() {
    if (lockCount === 0) return;
    lockCount -= 1;
    if (lockCount === 0) {
      document.body.style.overflow = savedOverflow || '';
      document.body.style.paddingRight = savedPaddingRight || '';
      savedOverflow = null;
      savedPaddingRight = null;
    }
  }

  // Attach a MutationObserver to each matching <dialog> so its `open` attribute
  // toggle drives lock/unlock automatically. Idempotent — safe to call multiple times.
  function autoLockOnDialog(selector) {
    var dialogs = document.querySelectorAll(selector || 'dialog');
    dialogs.forEach(function (d) {
      if (d.__jn_body_lock_observed) return;
      d.__jn_body_lock_observed = true;
      var locked = false;
      var observer = new MutationObserver(function () {
        if (d.open && !locked) { lock(); locked = true; }
        else if (!d.open && locked) { unlock(); locked = false; }
      });
      observer.observe(d, { attributes: true, attributeFilter: ['open'] });
      // Catch the case where the dialog was already open at observe time.
      if (d.open) { lock(); locked = true; }
    });
  }

  window.JN = window.JN || {};
  window.JN.bodyLock = {
    lock: lock,
    unlock: unlock,
    autoLockOnDialog: autoLockOnDialog,
    // Exposed for tests / debugging only — do not depend on this externally.
    _getCount: function () { return lockCount; },
  };
})(window, document);
